import React, { useEffect, useState } from 'react';
import { Tab, Tabs, AppBar, Box, TextField, IconButton, Menu, MenuItem, alpha } from '@mui/material';
import SwipeableViews from 'react-swipeable-views';
import { useSelector } from 'react-redux';
import { Sort } from '@mui/icons-material';
import Iconify from '../../../../components/Iconify';
import Page from '../../../../components/Page';
import { dispatch } from '../../../../redux/store';
import { getTradeHistory } from '../../../../redux/slices/trades';
import useTrade from '../../../../hooks/useTrade';
import TradesList from './TradesList';
import MemoizedPortfolioHeader from './MemoizedPortfolioHeader';

const TRADE_SORT_KEY = 'tradeListSort';

const TradesPage = () => {
  // Only select what we need at the top level
  const priceChangeConfig = useSelector((state) => state.trade.priceChangeConfig);
  const tradeHistory = useSelector((state) => state.trade.tradeHistory);
  
  const [tabValue, setTabValue] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortType, setSortType] = useState(() => {
    const savedSort = localStorage.getItem(TRADE_SORT_KEY);
    return savedSort || 'default';
  });
  const { localSocket } = useTrade();

  // Update tabValue when the user clicks on a tab
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  // Update tabValue when the user swipes to a new tab
  const handleSwipeChangeIndex = (index) => {
    setTabValue(index);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (type) => {
    setSortType(type);
    handleSortClose();
    localStorage.setItem(TRADE_SORT_KEY, type);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(getTradeHistory());
      } catch (error) {
        console.error('Error fetching trades:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const tokenArray =
      tradeHistory && tradeHistory.length > 0
        ? [
          ...tradeHistory
            .filter((trade) => ['OPEN', 'PENDING'].includes(trade.type))
            .map((trade) => +trade?.script?.instrument_token),
        ]
        : [];

    if (tokenArray.length) {
      const payload = tokenArray.map((token) => +token);

      const sendSubscribeMessage = () => {
        console.log('Sending subscribe message:', payload);
        localSocket.send(JSON.stringify({ event: 'subscribe', data: payload }));
      };

      if (localSocket) {
        if (localSocket.readyState === WebSocket.OPEN) {
          sendSubscribeMessage();
        } else {
          localSocket.onopen = () => {
            // console.log('WebSocket connection opened');
            sendSubscribeMessage();
          };
        }
      } else {
        console.log('localSocket is not defined');
      }
    }

    return () => {
      if (localSocket && localSocket.readyState === WebSocket.OPEN) {
        if (tokenArray.length > 0) {
          console.log('Sending unsubscribe message:', tokenArray);
          localSocket.send(JSON.stringify({ event: 'unsubscribe', data: tokenArray.map((token) => +token) }));
        }
      }
    };
  }, [tradeHistory, localSocket, localSocket?.readyState]);

  return (
    <Page title="Portfolio" sx={{ px: 0, height: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Memoized Portfolio Header - only re-renders when needed */}
        <MemoizedPortfolioHeader />
        
        {/* Tab navigation */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
          <AppBar position="static" color="default" sx={{ position: 'sticky', top: 0, zIndex: 1, mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Tabs
                value={tabValue}
                onChange={handleChangeTab}
                indicatorColor="primary"
                textColor="primary"
                sx={{ width: '100%', display: 'flex' }}
              >
                <Tab label="ACTIVE TRADES" value={0} sx={{ flexGrow: 1, maxWidth: 'none' }} />
                <Tab label="ACTIVE LIMITS" value={1} sx={{ flexGrow: 1, maxWidth: 'none' }} />
              </Tabs>

              {/* Search and Sort Bar */}
              <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={handleSortClick}
                  sx={{
                    height: 32,
                    width: 32,
                    color: sortType !== 'default' ? 'primary.main' : 'text.disabled',
                    backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.04),
                    '&:hover': {
                      backgroundColor: 'background.paper',
                    },
                  }}
                >
                  <Sort />
                </IconButton>

                <TextField
                  fullWidth
                  placeholder="Search Symbols"
                  value={searchText}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <Iconify
                        icon="eva:search-fill"
                        sx={{
                          color: 'text.disabled',
                          width: 16,
                          height: 16,
                          mr: 0.5
                        }}
                      />
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 32,
                      borderRadius: 1,
                      backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.04),
                      fontSize: '0.875rem',
                      transition: 'all 0.2s',
                      '& fieldset': {
                        borderColor: (theme) => alpha(theme.palette.grey[500], 0.16),
                        borderWidth: 1,
                      },
                      '&:hover': {
                        backgroundColor: 'background.paper',
                        '& fieldset': {
                          borderColor: 'primary.lighter',
                        },
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'background.paper',
                        boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}`,
                        '& fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 1,
                        },
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&::placeholder': {
                        color: 'text.disabled',
                        opacity: 1,
                      },
                    },
                  }}
                />
              </Box>
            </Box>

            <Menu
              anchorEl={sortAnchorEl}
              open={Boolean(sortAnchorEl)}
              onClose={handleSortClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <MenuItem
                onClick={() => handleSortSelect('default')}
                sx={{
                  color: sortType === 'default' ? 'primary.main' : 'text.primary',
                  fontWeight: sortType === 'default' ? 600 : 400,
                }}
              >
                Default Order
              </MenuItem>
              <MenuItem
                onClick={() => handleSortSelect('atoz')}
                sx={{
                  color: sortType === 'atoz' ? 'primary.main' : 'text.primary',
                  fontWeight: sortType === 'atoz' ? 600 : 400,
                }}
              >
                Sort A to Z
              </MenuItem>
              <MenuItem
                onClick={() => handleSortSelect('lastUpdated')}
                sx={{
                  color: sortType === 'lastUpdated' ? 'primary.main' : 'text.primary',
                  fontWeight: sortType === 'lastUpdated' ? 600 : 400,
                }}
              >
                Sort by Last Updated
              </MenuItem>
            </Menu>
          </AppBar>
          
          {/* Swipeable views for tab content */}
          <SwipeableViews
            index={tabValue}
            onChangeIndex={handleSwipeChangeIndex}
            style={{ flexGrow: 1, height: 'calc(100% - 48px)', overflow: 'hidden' }}
            containerStyle={{ height: '100%' }}
            axis="x"
            resistance
          >
            {/* Active Trades List - Memoized to prevent unnecessary re-renders */}
            <TradesList 
              tabValue={0} 
              searchText={searchText} 
              sortType={sortType} 
              priceChangeConfig={priceChangeConfig} 
            />

            {/* Limits List - Memoized to prevent unnecessary re-renders */}
            <TradesList 
              tabValue={1} 
              searchText={searchText} 
              sortType={sortType} 
              priceChangeConfig={priceChangeConfig} 
            />
          </SwipeableViews>
        </Box>
      </Box>
    </Page>
  );
};

export default TradesPage;
